/**
 * Small DOM helper utilities for the vanilla JS UI layer.
 */

type ElementAttrs = Record<string, string | boolean | undefined>;

/** Create an HTML element with tag, class, attributes, and children. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: {
    className?: string;
    attrs?: ElementAttrs;
    text?: string;
    html?: string;
    children?: HTMLElement[];
    onClick?: () => void;
  } = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.className) node.className = opts.className;
  if (opts.attrs) {
    for (const [k, v] of Object.entries(opts.attrs)) {
      if (v === undefined || v === false) continue;
      node.setAttribute(k, v === true ? "" : String(v));
    }
  }
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.html !== undefined) node.innerHTML = opts.html;
  if (opts.children) {
    for (const child of opts.children) node.appendChild(child);
  }
  if (opts.onClick) {
    node.addEventListener("click", opts.onClick);
  }
  return node;
}

/** Clear all children of an element. */
export function clearChildren(node: HTMLElement): void {
  while (node.firstChild) {
    node.removeChild(node.firstChild);
  }
}

/** Query a single element within a parent, throwing if not found. */
export function qs<T extends HTMLElement = HTMLElement>(
  parent: ParentNode,
  selector: string,
): T {
  const found = parent.querySelector<T>(selector);
  if (!found) throw new Error(`Element not found: ${selector}`);
  return found;
}

type SpriteElement = HTMLImageElement | HTMLCanvasElement;

export type SpriteRenderOptions = {
  className?: string;
  alt?: string;
  width?: number;
  height?: number;
  paused?: boolean;
};

function createNativeSpriteElement(
  url: string,
  opts: SpriteRenderOptions,
): SpriteElement {
  const img = el("img", {
    className: `sprite-animated ${opts.className ?? ""}`.trim(),
    attrs: {
      src: url,
      alt: opts.alt ?? "",
      width: opts.width ? String(opts.width) : undefined,
      height: opts.height ? String(opts.height) : undefined,
    },
  }) as HTMLImageElement;
  if (opts.paused) img.classList.add("sprite-paused");
  return img;
}

function createFrameCycler(
  urls: string[],
  intervalMs: number,
  opts: SpriteRenderOptions,
): SpriteElement {
  const canvas = el("canvas", {
    className: `sprite-frame-container sprite-animated ${opts.className ?? ""}`.trim(),
  }) as HTMLCanvasElement;
  if (opts.width) canvas.width = opts.width;
  if (opts.height) canvas.height = opts.height;
  if (opts.paused) canvas.classList.add("sprite-paused");

  let current = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  const images: HTMLImageElement[] = [];
  let loaded = 0;

  function draw() {
    const ctx = canvas.getContext("2d");
    if (!ctx || images.length === 0) return;
    const img = images[current % images.length];
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  }

  function start() {
    if (timer || images.length === 0) return;
    timer = setInterval(() => {
      current = (current + 1) % images.length;
      draw();
    }, intervalMs);
  }

  function stop() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function onAllLoaded() {
    draw();
    if (!opts.paused) start();
  }

  for (const url of urls) {
    const img = new Image();
    img.onload = () => {
      loaded++;
      if (loaded === urls.length) onAllLoaded();
    };
    img.onerror = () => {
      loaded++;
      if (loaded === urls.length) onAllLoaded();
    };
    img.src = url;
    images.push(img);
  }

  canvas.dataset.spritePaused = opts.paused ? "true" : "false";
  (canvas as unknown as { spriteStart: () => void; spriteStop: () => void }).spriteStart = start;
  (canvas as unknown as { spriteStop: () => void }).spriteStop = stop;

  return canvas;
}

/** Create a DOM element for a resolved sprite. */
export function createSpriteElement(
  resolution: { kind: "native" | "frames"; urls: string[]; intervalMs: number },
  opts: SpriteRenderOptions = {},
): SpriteElement {
  if (resolution.kind === "native") {
    return createNativeSpriteElement(resolution.urls[0], opts);
  }
  return createFrameCycler(resolution.urls, resolution.intervalMs, opts);
}

/** Pause a sprite element (native or frame-cycler). */
export function pauseSprite(el: SpriteElement): void {
  el.classList.add("sprite-paused");
  if (el.tagName === "CANVAS") {
    const cycler = el as unknown as { spriteStop?: () => void };
    cycler.spriteStop?.();
  }
}

/** Resume a sprite element (native or frame-cycler). */
export function resumeSprite(el: SpriteElement): void {
  el.classList.remove("sprite-paused");
  if (el.tagName === "CANVAS") {
    const cycler = el as unknown as { spriteStart?: () => void };
    cycler.spriteStart?.();
  }
}
